import ENV from '@/lib/env';

export interface RegistrationEmailParams {
  providerName: string;
  patientId: string;
  patientName: string;
  dob: string;
  gender: string;
  mobile: string;
  email: string;
  registrationDate: string;
  acknowledgementDate: string;
  location: string;
  primaryProviderName: string;
  secondaryProviderName?: string;
}

export function buildRegistrationEmailHtml(params: RegistrationEmailParams): string {
  const {
    providerName,
    patientId,
    patientName,
    dob,
    gender,
    mobile,
    email,
    registrationDate,
    acknowledgementDate,
    location,
    primaryProviderName,
    secondaryProviderName,
  } = params;

  let resolvedBaseUrl = (ENV.VITE_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '') || 'https://survey-dev.monitorhealth.ai').replace(/\/$/, '');
  if (resolvedBaseUrl.includes('localhost') || resolvedBaseUrl.includes('127.0.0.1')) {
    resolvedBaseUrl = 'https://survey-dev.monitorhealth.ai';
  }
  const baseUrl = resolvedBaseUrl;

  // Format Dr. prefix cleanly
  const formatDr = (name?: string) => {
    if (!name || name === "Not assigned") return name || "Care Provider";
    const clean = name.trim();
    if (clean.toLowerCase().startsWith("dr.") || clean.toLowerCase().startsWith("dr ")) {
      return clean;
    }
    return `Dr. ${clean}`;
  };

  const drTitle = formatDr(providerName);
  const primaryDrFormatted = formatDr(primaryProviderName);
  const secondaryDrFormatted = secondaryProviderName ? formatDr(secondaryProviderName) : undefined;

  // Build public standalone printable report URL that does NOT require login
  const reportQuery = new URLSearchParams({
    id: patientId || '',
    name: patientName || '',
    dob: dob || '',
    gender: gender || '',
    mobile: mobile || '',
    email: email || '',
    regDate: registrationDate || '',
    ackDate: acknowledgementDate || '',
    loc: location || '',
    primary: primaryDrFormatted,
    ...(secondaryDrFormatted ? { secondary: secondaryDrFormatted } : {}),
    autoPrint: 'true',
  });
  const publicReportUrl = `${baseUrl}/patient-enrollment-report?${reportQuery.toString()}`;
  const chartUrl = `${baseUrl}/patients/${patientId}`;

  return `
<!DOCTYPE html>
<html lang="en" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="color-scheme" content="light">
  <meta name="supported-color-schemes" content="light">
  <title>New Patient ${patientName} registered - for ${drTitle}</title>
  <style>
    :root {
      color-scheme: light;
      supported-color-schemes: light;
    }
    body, table, td, p, a, li, blockquote {
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
    }
    /* Dark mode overrides: force light aesthetic with crisp contrasts */
    @media (prefers-color-scheme: dark) {
      .email-bg { background-color: #f1f5f9 !important; }
      .card-bg { background-color: #ffffff !important; }
      .header-cell { background-color: #1a7a4a !important; }
      .header-title { color: #ffffff !important; }
      .header-subtitle { color: #d1fae5 !important; }
      .badge-cell { background-color: #145d39 !important; color: #ffffff !important; }
      .text-dark { color: #111827 !important; }
      .text-muted { color: #4b5563 !important; }
      .info-card { background-color: #f8fafc !important; }
    }
    /* Outlook & webmail dark mode attribute overrides */
    [data-ogsc] .header-cell,
    [data-ogsb] .header-cell {
      background-color: #1a7a4a !important;
    }
    [data-ogsc] .header-title,
    [data-ogsb] .header-title {
      color: #ffffff !important;
    }
  </style>
</head>
<body class="email-bg" style="margin: 0; padding: 0; background-color: #f4f7f6; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #333333;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" class="email-bg" style="background-color: #f4f7f6; padding: 30px 10px;">
    <tr>
      <td align="center">
        <table role="presentation" width="100%" class="card-bg" style="max-width: 620px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid #e5e7eb;">
          
          <!-- Brand Header with solid fallback for dark mode -->
          <tr>
            <td class="header-cell" bgcolor="#1a7a4a" style="background-color: #1a7a4a; background: linear-gradient(135deg, #1a7a4a 0%, #145d39 100%); padding: 32px 36px; text-align: left;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                <tr>
                  <td>
                    <span class="header-title" style="font-size: 22px; font-weight: 800; color: #ffffff; letter-spacing: -0.5px; display: block;">MonitorHealth<span style="color: #6ee7b7;">.AI</span></span>
                    <p class="header-subtitle" style="margin: 4px 0 0 0; color: #d1fae5; font-size: 13px; font-weight: 500;">AllyPsychiatry Clinical Network</p>
                  </td>
                  <td align="right" valign="top">
                    <span class="badge-cell" style="display: inline-block; background-color: #238652; color: #ffffff; font-size: 11px; font-weight: 700; padding: 5px 12px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.5px; border: 1px solid rgba(255,255,255,0.3);">
                      New Enrollment
                    </span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Main Body -->
          <tr>
            <td style="padding: 36px 36px 20px 36px; background-color: #ffffff;">
              <h2 class="text-dark" style="margin: 0 0 10px 0; color: #111827; font-size: 20px; font-weight: 700; line-height: 1.3;">
                New Patient ${patientName} registered - for ${drTitle}
              </h2>
              <p class="text-muted" style="margin: 0 0 16px 0; color: #4b5563; font-size: 14px; line-height: 1.6;">
                Dear <strong>${drTitle}</strong>,
              </p>
              <p class="text-muted" style="margin: 0 0 24px 0; color: #4b5563; font-size: 14px; line-height: 1.6;">
                This notification confirms that a new patient has completed registration and acknowledged their consent to participate in Behavioral Health Integration Services in-between visits with the <strong>AllyPsychiatry Care Team</strong>.
              </p>

              <!-- Patient Identification Card -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" class="info-card" style="background-color: #f9fafb; border-radius: 12px; border: 1px solid #e5e7eb; margin-bottom: 24px;">
                <tr>
                  <td style="padding: 14px 20px; border-bottom: 1px solid #e5e7eb; background-color: #f3f4f6; border-top-left-radius: 12px; border-top-right-radius: 12px;">
                    <span style="font-size: 12px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px;">📋 Patient Identification</span>
                  </td>
                </tr>
                <tr>
                  <td style="padding: 18px 20px;">
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="6">
                      <tr>
                        <td width="38%" style="font-size: 13px; color: #6b7280; font-weight: 600;">Full Name:</td>
                        <td width="62%" class="text-dark" style="font-size: 14px; color: #111827; font-weight: 700;">${patientName}</td>
                      </tr>
                      <tr>
                        <td style="font-size: 13px; color: #6b7280; font-weight: 600;">Date of Birth:</td>
                        <td class="text-dark" style="font-size: 13px; color: #111827;">${dob} (${gender})</td>
                      </tr>
                      <tr>
                        <td style="font-size: 13px; color: #6b7280; font-weight: 600;">Mobile:</td>
                        <td class="text-dark" style="font-size: 13px; color: #111827; font-weight: 600;">${mobile}</td>
                      </tr>
                      <tr>
                        <td style="font-size: 13px; color: #6b7280; font-weight: 600;">Email:</td>
                        <td class="text-dark" style="font-size: 13px; color: #111827;">${email || 'Not provided'}</td>
                      </tr>
                      <tr>
                        <td style="font-size: 13px; color: #6b7280; font-weight: 600;">Registration Date:</td>
                        <td class="text-dark" style="font-size: 13px; color: #111827;">${registrationDate}</td>
                      </tr>
                      <tr>
                        <td style="font-size: 13px; color: #6b7280; font-weight: 600;">Acknowledgement Date:</td>
                        <td style="font-size: 13px; color: #166534; font-weight: 600;">${acknowledgementDate}</td>
                      </tr>
                      <tr>
                        <td style="font-size: 13px; color: #6b7280; font-weight: 600;">Location:</td>
                        <td class="text-dark" style="font-size: 13px; color: #111827;">${location}</td>
                      </tr>
                      <tr>
                        <td style="font-size: 13px; color: #6b7280; font-weight: 600;">Primary Provider:</td>
                        <td style="font-size: 13px; color: #145d39; font-weight: 700;">${primaryDrFormatted}</td>
                      </tr>
                      ${secondaryDrFormatted ? `
                      <tr>
                        <td style="font-size: 13px; color: #6b7280; font-weight: 600;">Secondary Provider:</td>
                        <td class="text-dark" style="font-size: 13px; color: #111827; font-weight: 600;">${secondaryDrFormatted}</td>
                      </tr>` : ''}
                    </table>
                  </td>
                </tr>
              </table>

              <!-- Consents Status -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #ecfdf5; border-radius: 12px; border: 1px solid #a7f3d0; margin-bottom: 26px; padding: 14px 18px;">
                <tr>
                  <td>
                    <p style="margin: 0; font-size: 13px; color: #065f46; font-weight: 600; line-height: 1.5;">
                      ✅ Consents Verified: Behavioral Health Integration Services participation consent acknowledged &amp; HIPAA clinical communication consent recorded.
                    </p>
                  </td>
                </tr>
              </table>

              <!-- Primary CTA: Direct Download / Print Patient Enrollment Report without requiring Login -->
              <div style="text-align: center; margin: 28px 0 16px 0;">
                <a href="${publicReportUrl}" target="_blank" style="display: inline-block; background-color: #1a7a4a; background: linear-gradient(135deg, #1a7a4a 0%, #15803d 100%); color: #ffffff !important; text-decoration: none; font-size: 15px; font-weight: 700; padding: 14px 32px; border-radius: 10px; box-shadow: 0 4px 12px rgba(26,122,74,0.35); letter-spacing: 0.2px;">
                  📄 View &amp; Download Patient Enrollment Report (PDF)
                </a>
                <p style="margin: 8px 0 0 0; font-size: 11px; color: #6b7280;">
                  (Opens printable report instantly &bull; No login required)
                </p>
              </div>

              <!-- Secondary Link to in-app EHR -->
              <p style="text-align: center; margin: 16px 0 20px 0;">
                <a href="${chartUrl}" target="_blank" style="color: #2563eb; text-decoration: none; font-size: 12px; font-weight: 600;">
                  Open Patient Chart in MonitorHealth.AI EHR (Staff Login Required) &rarr;
                </a>
              </p>

            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color: #f9fafb; padding: 24px 36px; border-top: 1px solid #e5e7eb; text-align: center;">
              <p style="margin: 0 0 6px 0; font-size: 11px; color: #6b7280; font-weight: 700; text-transform: uppercase;">
                Confidentiality &amp; HIPAA Compliance Notice
              </p>
              <p style="margin: 0; font-size: 11px; color: #9ca3af; line-height: 1.5;">
                This communication contains Protected Health Information (PHI) intended solely for the authorized healthcare provider named above. If you are not the intended recipient, any disclosure, copying, distribution, or use of this information is strictly prohibited under HIPAA and federal law.
              </p>
              <p style="margin: 10px 0 0 0; font-size: 11px; color: #9ca3af;">
                &copy; ${new Date().getFullYear()} MonitorHealth.AI &bull; AllyPsychiatry Care Network
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
}
